import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ErrorBoundary } from '@/components/core/common/boundaries/ErrorBoundary';
import { PanelFallback } from '@/components/core/common/feedback/PanelFallback';

/**
 * Per-route error boundary used inside `AppLayout`'s content slot.
 *
 * When a page component crashes the surrounding `AppLayout` (sidebar +
 * topbar) stays mounted and clickable so the user can navigate away
 * instead of reloading the whole app. The fallback uses the panel-size
 * `ErrorState` to fit inside the content area rather than overflowing
 * with a `min-h-screen` page-variant.
 *
 * Sits one level below the root `<ErrorBoundary>` in `App.tsx` - that
 * boundary catches anything that escapes here (e.g. a crash in
 * `AppLayout` itself, or in a context provider).
 *
 * The inner `<ErrorBoundary>` is `key`ed on `location.pathname` so it
 * remounts (with fresh `hasError: false` state) every time the user
 * navigates. Without this, clicking "Go home" would update the URL but
 * leave the boundary stuck in its error state, because class-component
 * boundaries don't reset on prop changes.
 */
export function PageBoundary({ children }: { children: ReactNode }) {
  return <PageBoundaryInner>{children}</PageBoundaryInner>;
}

function PageBoundaryInner({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <ErrorBoundary
      key={location.pathname}
      fallback={
        <PanelFallback
          variant='page'
          title='This page hit an error'
          description='Try reloading. If it keeps happening, head home and start fresh.'
          primary={{
            label: 'Reload page',
            onClick: () => window.location.reload(),
          }}
          secondary={{
            label: 'Go home',
            onClick: () => navigate('/'),
          }}
        />
      }
    >
      {children}
    </ErrorBoundary>
  );
}
